<template>
  <el-dialog
    :model-value="visible"
    :before-close="handleClose"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :width="width"
    :modal="true"
    :top="top"
    :append-to-body="appendToBody"
    :fullscreen="false"
    :modal-class="extendHeight ? 'dd-modal__extend-height' : ''"
    class="dd-modal dd-modal--mobile"
  >
    <template #header>
      <span class="dd-modal__title">{{ title }}</span>
      <span class="dd-modal__subtitle" v-html="subtitle" />
    </template>
    <div ref="body" class="dd-modal__body">
      <div class="dd-modal__container">
        <div class="dd-modal__content">
          <slot />
        </div>
      </div>
    </div>
    <template v-if="$slots.footer" #footer>
      <div class="dd-modal__footer">
        <slot name="footer" />
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'DdModal',

  props: {
    /*
    * @description: Title of modal.
    */
    title: {
      type: String,
      default: '',
    },
    /*
    * @description: Subtitle of modal. Can pass in HTML as well.
    */
    subtitle: {
      type: String,
      default: '',
    },
    /*
    * @description: 'Small' (500px), 'Medium' (624px), 'Large' (800px), 'Grow' (70%)
    */
    desktopSizing: {
      type: String,
      default: 'medium',
    },
    /*
    * @description: If true, height is 800px instead of 600px
    */
    extendHeight: {
      type: Boolean,
      default: false,
    },
    /*
    * @description: Margin-top of modal.
    */
    top: {
      type: String,
      default: '5vh',
    },
    /*
    * @description: Dynamic props use for open/close modal
    */
    visible: {
      type: Boolean,
      default: true,
    },
    /*
    * @description: If false disabled "x" button and need implement attr beforeClose for wrapper
    */
    selfClose: {
      type: Boolean,
      default: true,
    },
    /*
    * @description: function wrapper for click on "X" button. Only work if selfClose equal false
    */
    beforeClose: {
      type: Function,
      default: () => false,
    },
    /*
    * @description: When true, the dialog is appended to document.body so it escapes any
    * stacking context created by an ancestor (e.g. a Popper-positioned dropdown). Leave
    * false (the default) for modals rendered in a clean parent tree.
    */
    appendToBody: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['handleClose', 'update:visible'],

  computed: {
    width(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '70%'
      }

      if (this.desktopSizing === 'small') {
        return '500px'
      } else if (this.desktopSizing === 'medium') {
        return '624px'
      } else if (this.desktopSizing === 'large') {
        return '800px'
      } else if (this.desktopSizing === 'grow') {
        return '70%'
      }
      return undefined
    },
  },

  watch: {
    /**
     * Remove body scroll when modals
     */
    visible(value: boolean) {
      if (value === true) {
        this.$bodyScroll.disable()
      } else {
        this.$bodyScroll.enable()
        this.handleClose()
      }
    },
  },

  methods: {
    handleClose() {
      // el-dialog teleports itself, so $el isn't in the dialog; find its body from our content instead
      const content = this.$refs.body as HTMLElement | undefined
      const body = content?.closest('.el-dialog__body') as HTMLElement | null
      if (body) {
        body.scrollTop = 0
      }

      this.$emit('handleClose', true)

      if (this.selfClose) {
        this.$emit('update:visible', false)
      } else {
        // @ts-ignore
        this.beforeClose()
      }
    },
  },
})
</script>

<style lang="scss">
.dd-modal {
  min-width: 320px;
  height: auto;
  max-height: 600px;
  margin-bottom: 5vh;
  background-color: var(--color-background-header);

  &__extend-height {
    .el-dialog {
      min-height: 600px;
      max-height: 90vh;
    }
  }

  &.el-dialog {
    display: flex;
    flex-direction: column;
    margin-top: 5vh;
    margin-bottom: 5vh;
  }

  .el-dialog__header {
    background-color: var(--color-background-header);
    position: relative;
    display: flex;
    flex-flow: column;
    justify-content: center;
    padding: 15px 40px;
    border-bottom: solid 1px $silver-two;

    .el-dialog__headerbtn i.el-dialog__close.el-icon.el-icon-close {
      font-family: 'icons' !important;
      font-weight: 400;
      color: var(--color-text-third);

      &::before {
        pointer-events: none;
        content: '\f10c';
      }
    }
  }

  .el-dialog__body {
    background-color: var(--color-background-header);
    overflow-y: auto;
    word-break: normal;
  }

  .el-dialog__footer {
    background-color: var(--color-background-header);
    border-top: solid 1px $silver-two;
    padding-top: 20px;
  }

  &__title {
    @include lettering--h3;
  }

  &__subtitle {
    @include lettering--p-sm;
    color: var(--color-text-primary);
  }

  &__body {
    height: auto;
    overflow: hidden !important;
    transition: box-shadow 200ms;
  }

  &__footer {
    > span {
      display: flex;
      justify-content: flex-end;
      flex-direction: row;
    }
  }

  &__container {
    width: auto;
    height: 100%;
    padding: 0 24px;
    overflow-x: hidden;
  }

  &__content {
    padding-top: 16px;
    padding-bottom: 24px;
  }
}

/* Modal masks. The dialog is inside the overlay, so the overlay can't use opacity. */
.el-overlay {
  background-color: rgba($color--gray-90, 0.8) !important;
}
</style>
